import { getLucideIconName, getMarkerColor, PIN_CATEGORIES } from '@/lib/pin-categories';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import * as LucideIcons from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { ensureGoogleMaps } from './init';

interface PlaceMarker {
  id: string;
  name: string;
  lat: number;
  lng: number;
  pinStyle: string;
  photoUrl?: string;
  rating?: number;
  address: string;
}

interface UnifiedMapProps {
  places: PlaceMarker[];
  apiKey: string;
  onMarkerClick?: (placeId: string) => void;
}

export const UnifiedMap: React.FC<UnifiedMapProps> = ({ places, apiKey, onMarkerClick }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!mapRef.current || !apiKey || places.length === 0) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Load Google Maps API (singleton - loads once)
        const google = await ensureGoogleMaps(apiKey);
        if (cancelled || !mapRef.current) return;

        // Import the maps and marker libraries (type-safe)
        const { Map } = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;
        const { AdvancedMarkerElement, PinElement } = (await google.maps.importLibrary(
          'marker'
        )) as google.maps.MarkerLibrary;

        // Create the map
        const map = new Map(mapRef.current, {
          zoom: 12,
          mapId: 'GUIDEBOOK_UNIFIED_MAP',
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: true,
          fullscreenControl: false,
        });

        // Add CSS to fix InfoWindow centering and padding
        if (!document.getElementById('infowindow-centering-fix')) {
          const style = document.createElement('style');
          style.id = 'infowindow-centering-fix';
          style.textContent = `
            /* fully neutralize the InfoWindow container chrome */
            .gm-style .gm-style-iw,
            .gm-style .gm-style-iw-a,
            .gm-style .gm-style-iw-ch,
            .gm-style .gm-style-iw-c {
              background: transparent !important;
              border: 0 !important;
              box-shadow: none !important;
              padding: 0 !important;
              border-radius: 0 !important;
            }
            /* inner scroll container */
            .gm-style .gm-style-iw-d {
              overflow: visible !important;
              padding: 0 !important;
              max-height: none !important;
            }
            /* tail + base */
            .gm-style .gm-style-iw-t {
              top: 0 !important;
            }
            .gm-style .gm-style-iw-tc,
            .gm-style .gm-style-iw-tc::after {
              display: none !important;
            }
            /* close button placement */
            .gm-style .gm-ui-hover-effect {
              display: none !important;
            }
          `;
          document.head.appendChild(style);
        }

        // Create markers for all places
        const markers: google.maps.marker.AdvancedMarkerElement[] = [];
        const isMobile = window.innerWidth < 640;
        // On mobile, ensure the InfoWindow fits within viewport with padding for map controls and close button
        const maxWidth = isMobile ? Math.min(240, window.innerWidth - 120) : 320;
        const infoWindow = new google.maps.InfoWindow({
          maxWidth: maxWidth,
        });

        places.forEach((place) => {
          const markerColor = getMarkerColor(place.pinStyle);
          const lucideIconName = getLucideIconName(place.pinStyle);

          // Render Lucide icon as SVG element
          const IconComponent = (LucideIcons as any)[lucideIconName] as React.ComponentType<any>;
          const iconSvgString = renderToStaticMarkup(<IconComponent size={18} strokeWidth={2.5} color="#ffffff" />);

          // Create a container for the SVG
          const iconContainer = document.createElement('div');
          iconContainer.innerHTML = iconSvgString;
          iconContainer.style.display = 'flex';
          iconContainer.style.alignItems = 'center';
          iconContainer.style.justifyContent = 'center';
          const svgElement = iconContainer.firstElementChild as HTMLElement;

          // Create a custom pin with color and Lucide icon glyph
          const pinElement = new PinElement({
            background: markerColor,
            borderColor: '#ffffff',
            glyph: svgElement, // Use Lucide SVG icon
            scale: 1.2, // Make pins slightly larger to show icon better
          });

          const marker = new AdvancedMarkerElement({
            map: map,
            position: { lat: place.lat, lng: place.lng },
            title: place.name,
            content: pinElement.element,
          });

          // Add click listener
          marker.addListener('click', () => {
            // Create enhanced info window with photo
            const wrapper = document.createElement('div');
            wrapper.style.margin = '0';
            wrapper.style.padding = '0';
            wrapper.style.display = 'flex';
            wrapper.style.justifyContent = 'center';
            wrapper.style.alignItems = 'flex-start';

            const card = document.createElement('div');
            const cardWidth = isMobile ? Math.min(240, window.innerWidth - 120) : 320;
            card.style.width = `${cardWidth}px`;
            card.style.maxWidth = `${cardWidth}px`;
            card.style.background = '#ffffff';
            card.style.borderRadius = '16px';
            card.style.boxShadow = '0 16px 42px rgba(15, 23, 42, 0.18)';
            card.style.display = 'flex';
            card.style.flexDirection = 'column';
            card.style.overflow = 'hidden';
            card.style.margin = '0';
            card.style.padding = '0';
            card.style.position = 'relative';

            const closeButton = document.createElement('button');
            closeButton.type = 'button';
            closeButton.setAttribute('aria-label', 'Close');
            closeButton.textContent = '×';
            closeButton.style.position = 'absolute';
            closeButton.style.top = isMobile ? '10px' : '12px';
            closeButton.style.right = isMobile ? '10px' : '12px';
            closeButton.style.width = isMobile ? '28px' : '32px';
            closeButton.style.height = isMobile ? '28px' : '32px';
            closeButton.style.borderRadius = '50%';
            closeButton.style.border = '1px solid rgba(148, 163, 184, 0.35)';
            closeButton.style.background = 'rgba(255, 255, 255, 0.92)';
            closeButton.style.color = '#111827';
            closeButton.style.fontSize = isMobile ? '18px' : '20px';
            closeButton.style.lineHeight = '1';
            closeButton.style.display = 'flex';
            closeButton.style.alignItems = 'center';
            closeButton.style.justifyContent = 'center';
            closeButton.style.cursor = 'pointer';
            closeButton.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.12)';
            closeButton.style.transition = 'transform 0.15s ease, box-shadow 0.15s ease';

            closeButton.addEventListener('mouseover', () => {
              closeButton.style.transform = 'scale(1.05)';
              closeButton.style.boxShadow = '0 6px 16px rgba(15, 23, 42, 0.18)';
            });
            closeButton.addEventListener('mouseout', () => {
              closeButton.style.transform = 'scale(1)';
              closeButton.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.12)';
            });
            closeButton.addEventListener('click', () => {
              infoWindow.close();
            });

            wrapper.appendChild(card);
            card.appendChild(closeButton);

            if (place.photoUrl) {
              const photoContainer = document.createElement('div');
              photoContainer.style.width = '100%';
              photoContainer.style.height = isMobile ? '132px' : '180px';
              photoContainer.style.overflow = 'hidden';

              const photo = document.createElement('img');
              photo.src = place.photoUrl;
              photo.alt = place.name;
              photo.style.width = '100%';
              photo.style.height = '100%';
              photo.style.objectFit = 'cover';
              photo.style.objectPosition = 'center';

              photoContainer.appendChild(photo);
              card.appendChild(photoContainer);
            }

            const contentArea = document.createElement('div');
            const contentPadding = isMobile ? '16px 16px 16px 16px' : '18px 20px';
            contentArea.style.padding = contentPadding;
            contentArea.style.display = 'flex';
            contentArea.style.flexDirection = 'column';
            contentArea.style.gap = isMobile ? '10px' : '14px';

            // Title
            const title = document.createElement('div');
            title.style.fontWeight = '600';
            title.style.fontSize = isMobile ? '16px' : '18px';
            title.style.color = '#111827';
            title.textContent = place.name;

            // Rating and address container
            const infoRow = document.createElement('div');
            infoRow.style.display = 'flex';
            infoRow.style.alignItems = 'center';
            infoRow.style.gap = isMobile ? '6px' : '8px';
            infoRow.style.fontSize = isMobile ? '12px' : '13px';
            infoRow.style.color = '#6b7280';
            infoRow.style.flexWrap = 'wrap';

            // Rating (if exists)
            if (place.rating) {
              const rating = document.createElement('div');
              rating.style.display = 'flex';
              rating.style.alignItems = 'center';
              rating.style.gap = '4px';
              rating.innerHTML = `<span style="color: #fbbf24; font-size: 14px;">★</span> <strong style="color: #374151;">${place.rating.toFixed(1)}</strong>`;
              infoRow.appendChild(rating);

              const divider = document.createElement('span');
              divider.textContent = '•';
              divider.style.color = '#d1d5db';
              infoRow.appendChild(divider);
            }

            // Category - show friendly label
            const category = document.createElement('div');
            const pinCategory = PIN_CATEGORIES.find((cat) => cat.value === place.pinStyle);
            category.textContent = pinCategory?.label || place.pinStyle.replace(/_/g, ' ');
            infoRow.appendChild(category);

            // Address
            const address = document.createElement('div');
            address.style.fontSize = isMobile ? '11px' : '12px';
            address.style.color = '#9ca3af';
            address.style.lineHeight = '1.5';
            address.textContent = place.address;

            // View Details button
            const viewButton = document.createElement('button');
            viewButton.textContent = 'View Details';
            viewButton.style.padding = isMobile ? '9px 12px' : '11px 18px';
            viewButton.style.background = '#3b82f6';
            viewButton.style.color = 'white';
            viewButton.style.border = 'none';
            viewButton.style.borderRadius = '8px';
            viewButton.style.cursor = 'pointer';
            viewButton.style.fontSize = isMobile ? '13px' : '15px';
            viewButton.style.fontWeight = '500';
            viewButton.style.width = '100%';
            viewButton.style.transition = 'background 0.2s';
            viewButton.style.marginTop = isMobile ? '6px' : '8px';

            viewButton.addEventListener('mouseover', () => {
              viewButton.style.background = '#2563eb';
            });
            viewButton.addEventListener('mouseout', () => {
              viewButton.style.background = '#3b82f6';
            });

            viewButton.addEventListener('click', () => {
              if (onMarkerClick) {
                onMarkerClick(place.id);
              }
            });

            contentArea.appendChild(title);
            contentArea.appendChild(infoRow);
            contentArea.appendChild(address);
            contentArea.appendChild(viewButton);

            card.appendChild(contentArea);

            infoWindow.setContent(wrapper);
            infoWindow.open({
              anchor: marker,
              map,
            });
            infoWindow.setOptions({
              pixelOffset: new google.maps.Size(0, -24),
            });
          });

          markers.push(marker);
        });

        // Fit map bounds to show all markers
        const bounds = new google.maps.LatLngBounds();
        places.forEach((place) => {
          bounds.extend({ lat: place.lat, lng: place.lng });
        });
        map.fitBounds(bounds);

        // Add some padding to the bounds
        const padding = { top: 50, right: 50, bottom: 50, left: 50 };
        map.fitBounds(bounds, padding);

        // Add marker clustering if there are many markers
        if (markers.length > 10) {
          new MarkerClusterer({
            map,
            markers,
          });
        }

        setLoading(false);
      } catch (err: any) {
        if (!cancelled) {
          console.error('Error loading map:', err);
          setError('Failed to load map. Please try again later.');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [places, apiKey, onMarkerClick]);

  if (places.length === 0) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f9fafb',
          color: '#6b7280',
        }}
      >
        <p>No places to display on the map.</p>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {loading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f9fafb',
            zIndex: 10,
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p style={{ marginTop: '12px', fontSize: '14px', color: '#6b7280' }}>
              Loading map with {places.length} places...
            </p>
          </div>
        </div>
      )}

      {error && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fef2f2',
            padding: '20px',
            textAlign: 'center',
          }}
        >
          <div>
            <p style={{ color: '#dc2626', fontWeight: 500 }}>{error}</p>
          </div>
        </div>
      )}

      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};
